import { LSelect } from "./ui/LSelect";
import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { loyaltyService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2, Gift } from "lucide-react";

const TYPES = ["Points", "Cashback", "Membership", "Referral", "Hybrid"];
const STATUSES = ["Draft", "Active", "Inactive"];
const STATUS_COLOR = { Active: "green", Draft: "amber", Inactive: "slate" };
const EMPTY = { name: "", type: "Points", status: "Draft", description: "", amountPer: 100, points: 10, pointValue: 0.25, tenantId: "" };

function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-foreground mb-1">{label}</label>{children}</div>;
}

export default function LoyaltyProgramsPage() {
  const { hasPermission, user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const { format } = useCurrency();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const canManage = hasPermission("LOYALTY.CREATE") || hasPermission("LOYALTY.UPDATE");

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await loyaltyService.programs()).items || []); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, type: p.type, status: p.status, description: p.description || "",
      amountPer: p.earnRate?.amountPer ?? 100, points: p.earnRate?.points ?? 10, pointValue: p.pointValue ?? 0.25, tenantId: p.tenantId });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Program name is required"); return; }
    setSaving(true);
    const payload = {
      name: form.name, type: form.type, status: form.status, description: form.description,
      earnRate: { amountPer: Number(form.amountPer) || 0, points: Number(form.points) || 0 },
      pointValue: Number(form.pointValue) || 0,
    };
    if (isSuper && form.tenantId) payload.tenantId = form.tenantId;
    try {
      if (editing) await loyaltyService.updateProgram(editing.id, payload);
      else await loyaltyService.createProgram(payload);
      toast.success(editing ? "Program updated" : "Program created");
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete program "${p.name}"?`)) return;
    try { await loyaltyService.deleteProgram(p.id); toast.success("Program deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const columns = [
    { key: "name", header: "Program", render: (p) => <span className="font-medium text-foreground">{p.name}</span> },
    { key: "programCode", header: "Code", render: (p) => <span className="font-mono text-xs text-muted-foreground">{p.programCode}</span> },
    { key: "type", header: "Type", render: (p) => <Badge color="blue">{p.type}</Badge> },
    { key: "earnRate", header: "Earn Rate", render: (p) => <span className="text-sm">{format(p.earnRate?.amountPer || 0)} = {p.earnRate?.points || 0} pts</span> },
    { key: "pointValue", header: "Point Value", render: (p) => <span className="text-sm">{format(p.pointValue || 0)}</span> },
    { key: "status", header: "Status", render: (p) => <Badge color={STATUS_COLOR[p.status] || "slate"}>{p.status}</Badge> },
    { key: "actions", header: "", render: (p) => canManage && (
      <div className="flex gap-1 justify-end">
        <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground" data-testid={`program-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
        {hasPermission("LOYALTY.ADMIN") && <button onClick={() => remove(p)} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive" data-testid={`program-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>}
      </div>
    ) },
  ];

  return (
    <div data-testid="loyalty-programs-page">
      <PageHeader title="Loyalty Programs" subtitle="Configure points, cashback, membership, referral & hybrid programs"
        actions={hasPermission("LOYALTY.CREATE") && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="program-create-button">
            <Plus className="h-4 w-4" /> New Program
          </button>
        )} />

      <DataTable testid="loyalty-programs-table" rows={rows} loading={loading} columns={columns}
        empty="No loyalty programs yet — create your first program." />

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="program-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative bg-card border border-border rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card">
              <div className="flex items-center gap-2"><Gift className="h-5 w-5 text-primary" /><h3 className="font-semibold text-foreground">{editing ? "Edit Program" : "New Loyalty Program"}</h3></div>
              <button onClick={() => setOpen(false)} data-testid="program-modal-close"><X className="h-5 w-5 text-muted-foreground hover:text-foreground" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <Field label="Program Name">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" data-testid="program-name-input" placeholder="e.g. Acme Rewards" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Type">
                  <LSelect value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="program-type-select">
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </LSelect>
                </Field>
                <Field label="Status">
                  <LSelect value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="program-status-select">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </LSelect>
                </Field>
              </div>
              <Field label="Description">
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
              </Field>
              <div className="rounded-lg bg-muted/40 border border-border p-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Earn Rule</p>
                <div className="grid grid-cols-3 gap-3 items-end">
                  <Field label="Spend"><input type="number" value={form.amountPer} onChange={(e) => setForm({ ...form, amountPer: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="program-amountper-input" /></Field>
                  <Field label="Points"><input type="number" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="program-points-input" /></Field>
                  <Field label="1 pt ="><input type="number" step="0.01" value={form.pointValue} onChange={(e) => setForm({ ...form, pointValue: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="program-pointvalue-input" /></Field>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Spend {format(Number(form.amountPer) || 0)} → earn {form.points || 0} points · 1 point = {format(Number(form.pointValue) || 0)}</p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-border flex justify-end gap-2 sticky bottom-0 bg-card">
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted">Cancel</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid="program-save-button">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save Changes" : "Create Program"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
